import { format } from 'date-fns'
import { useState } from 'react'

import { formatTimezoneDate, formatTimezoneTime, type AppTimezonePreference } from '@sparkai/utils'

import { Tooltip } from '../../General/Tooltip'
import { Icon } from '../../Icon'

type ShiftBadgeProps = Readonly<{
  id: string
  startDate?: string
  endDate?: string
  inactivityTime?: string
  timezonePreference?: AppTimezonePreference
}>

export function ShiftBadge({ id, startDate, endDate, inactivityTime, timezonePreference }: ShiftBadgeProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()

    try {
      await navigator.clipboard.writeText(id)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy shift ID:', err)
    }
  }

  const isValidDate = (dateString?: string): boolean => {
    if (!dateString) return false
    const date = new Date(dateString)
    return !Number.isNaN(date.getTime())
  }

  const getTooltipContent = () => {
    if (copied) return 'Copied!'
    if (startDate && endDate) {
      if (!isValidDate(startDate) || !isValidDate(endDate)) {
        return 'Click to copy shift ID'
      }

      const validInactivityTime = inactivityTime && isValidDate(inactivityTime) ? inactivityTime : undefined
      const startFormatted = timezonePreference
        ? formatTimezoneDate(startDate, timezonePreference, { datePattern: 'MMM d' })
        : format(new Date(startDate), 'MMM d')
      const startTime = timezonePreference
        ? formatTimezoneTime(startDate, timezonePreference)
        : format(new Date(startDate), 'h:mm aaa')
      const endTime = timezonePreference
        ? formatTimezoneTime(endDate, timezonePreference, { includeTimezoneIndicator: !validInactivityTime })
        : format(new Date(endDate), 'h:mm aaa')
      let newEndTime: string | null = null
      if (validInactivityTime && timezonePreference) {
        newEndTime = formatTimezoneTime(validInactivityTime, timezonePreference, {
          includeTimezoneIndicator: true,
        })
      } else if (validInactivityTime) {
        newEndTime = format(new Date(validInactivityTime), 'h:mm aaa')
      }

      return (
        <div className='text-center'>
          <div>
            {startFormatted} - {startTime} to{' '}
            {newEndTime ? (
              <>
                <span className='line-through'>{endTime}</span> {newEndTime}
              </>
            ) : (
              endTime
            )}
          </div>
          <div className='mt-1 text-xs opacity-75'>Click to copy shift ID</div>
        </div>
      )
    }
    return 'Click to copy shift ID'
  }

  return (
    <Tooltip content={getTooltipContent()} placement='top' theme='dark'>
      <button
        type='button'
        onClick={handleCopy}
        className='inline-flex cursor-pointer items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200'
      >
        {id}
        <Icon name='copy' className='ml-1 h-3 w-3' />
      </button>
    </Tooltip>
  )
}
